import React, { useEffect, useRef, useState } from 'react';
import { ApiError, isRecord, type AdminApiClient } from './api-client';
import { ActionButton, ConfirmDialog, Notice } from './admin-feedback';
import './account-center.css';

interface MyAccount {
  id: string;
  username: string | null;
  displayName: string;
  organizationName: string | null;
  roleNames: string[];
  status: string;
  version: number;
  credentialEnrollment: 'PENDING' | 'ENROLLED';
  passwordChangedAt: string | null;
}
function parseAccount(value: unknown): MyAccount {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    !value.id ||
    (value.username !== null && typeof value.username !== 'string') ||
    typeof value.displayName !== 'string' ||
    !Number.isInteger(value.version) ||
    Number(value.version) < 0 ||
    !Array.isArray(value.roleNames)
  )
    throw new Error('个人资料未完整读取，请重新读取后再操作。');
  return {
    id: value.id,
    username: value.username,
    displayName: value.displayName,
    organizationName: typeof value.organizationName === 'string' ? value.organizationName : null,
    roleNames: value.roleNames.filter((name): name is string => typeof name === 'string'),
    status: typeof value.status === 'string' ? value.status : '',
    version: Number(value.version),
    credentialEnrollment: value.credentialEnrollment === 'ENROLLED' ? 'ENROLLED' : 'PENDING',
    passwordChangedAt: typeof value.passwordChangedAt === 'string' ? value.passwordChangedAt : null,
  };
}
function accountError(error: unknown, fallback: string, secrets: readonly string[] = []): string {
  if (error instanceof ApiError && error.status === 401) return '当前登录已失效，请重新登录。';
  if (error instanceof ApiError && error.status === 403)
    return '当前账号不能执行此操作，请联系平台管理员。';
  if (error instanceof ApiError && error.status === 409)
    return '账号资料已更新，请重新读取后再操作。';
  if (
    error instanceof Error &&
    /[\u3400-\u9fff]/u.test(error.message) &&
    !secrets.some((secret) => secret && error.message.includes(secret))
  )
    return error.message;
  return fallback;
}
async function accountRequest<T>(
  api: AdminApiClient,
  path: string,
  init: RequestInit,
  signal: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  let rejectCancellation: ((error: Error) => void) | undefined;
  const cancellation = new Promise<never>((_, reject) => {
    rejectCancellation = reject;
  });
  const cancel = () => {
    controller.abort();
    rejectCancellation?.(new Error('Request cancelled'));
  };
  if (signal.aborted) cancel();
  signal.addEventListener('abort', cancel, { once: true });
  const timeout = new Promise<never>((_, reject) => {
    deadline = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(new Error('请求超时，请重新读取确认后再操作。'));
    }, 15_000);
  });
  try {
    return await Promise.race([
      api.request<T>(path, { ...init, signal: controller.signal, cache: 'no-store' }),
      timeout,
      cancellation,
    ]);
  } catch (error) {
    if (timedOut) throw new Error('请求超时，请重新读取确认后再操作。', { cause: error });
    throw error;
  } finally {
    if (deadline) clearTimeout(deadline);
    signal.removeEventListener('abort', cancel);
  }
}
function passwordProblem(password: string, confirmation: string): string {
  if (password.length < 12 || password.length > 128) return '新密码需要 12–128 个字符。';
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password))
    return '新密码必须同时包含英文大写、小写和数字。';
  return password !== confirmation ? '两次输入的新密码不一致。' : '';
}
function changedAtLabel(value: string | null): string {
  if (!value) return '尚无密码修改记录';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    : '密码修改时间待确认';
}

export function AccountCenterPage({
  api,
  onPasswordChanged,
  onProfileChanged,
}: {
  api: AdminApiClient;
  onPasswordChanged?: () => void;
  onProfileChanged?: (profile: { displayName: string }) => void;
}) {
  const [profile, setProfile] = useState<MyAccount>();
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState(''),
    [newPassword, setNewPassword] = useState(''),
    [confirmationPassword, setConfirmationPassword] = useState('');
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [loadError, setLoadError] = useState(''),
    [feedback, setFeedback] = useState('');
  const [passwordError, setPasswordError] = useState(''),
    [confirmPasswordChange, setConfirmPasswordChange] = useState(false),
    [reauthenticationRequired, setReauthenticationRequired] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<() => void>();
  const operation = useRef<AbortController>(),
    generation = useRef(0),
    running = useRef(false),
    mounted = useRef(false);
  const dirty =
    Boolean(profile && displayName !== profile.displayName) ||
    Boolean(currentPassword || newPassword || confirmationPassword);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const blocked =
    busy ||
    loading ||
    Boolean(loadError) ||
    reauthenticationRequired ||
    !profile ||
    profile.status !== 'ACTIVE';
  const canChangePassword =
    Boolean(profile?.username) && profile?.credentialEnrollment === 'ENROLLED';
  const base = '/api/admin/m8/account';
  function clearPasswords() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmationPassword('');
    setPasswordError('');
    setConfirmPasswordChange(false);
  }
  async function read(signal: AbortSignal, context: number) {
    setLoading(true);
    setLoadError('');
    try {
      const next = parseAccount(await accountRequest(api, `${base}/me`, {}, signal));
      if (signal.aborted || generation.current !== context || !mounted.current) return undefined;
      setProfile(next);
      setDisplayName(next.displayName);
      return next;
    } catch (error) {
      if (!signal.aborted && generation.current === context && mounted.current) {
        setLoadError(accountError(error, '个人资料读取失败，请重试。'));
        throw error;
      }
    } finally {
      if (!signal.aborted && generation.current === context && mounted.current) setLoading(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    const context = ++generation.current;
    const controller = new AbortController();
    operation.current = controller;
    running.current = false;
    setBusy(false);
    setProfile(undefined);
    setDisplayName('');
    setFeedback('');
    setPendingNavigation(undefined);
    setReauthenticationRequired(false);
    clearPasswords();
    void read(controller.signal, context).catch(() => undefined);
    return () => {
      mounted.current = false;
      controller.abort();
      operation.current?.abort();
    };
  }, [api]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current || running.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    const navigation = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || event.ctrlKey || event.metaKey || event.shiftKey)
        return;
      const link = event.target.closest<HTMLAnchorElement>('a[href]');
      if (!link || link.target === '_blank') return;
      const target = new URL(link.href, window.location.href);
      if (target.href === window.location.href || target.origin !== window.location.origin) return;
      if (!dirtyRef.current && !running.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (running.current) {
        setFeedback('正在确认账号资料，请等待当前操作完成后再离开。');
        return;
      }
      setConfirmPasswordChange(false);
      setPendingNavigation(() => () => window.location.assign(target.href));
    };
    const changedRoute = () => clearPasswords();
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', navigation, true);
    window.addEventListener('popstate', changedRoute);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', navigation, true);
      window.removeEventListener('popstate', changedRoute);
    };
  }, []);
  async function reload() {
    if (running.current || reauthenticationRequired) return;
    const controller = new AbortController();
    operation.current?.abort();
    operation.current = controller;
    const context = ++generation.current;
    clearPasswords();
    setFeedback('');
    try {
      await read(controller.signal, context);
    } catch {
      /* Retry remains visible. */
    } finally {
      if (operation.current === controller) operation.current = undefined;
    }
  }
  async function saveProfile() {
    if (running.current || blocked || !profile || displayName === profile.displayName) return;
    const nextName = displayName.trim();
    if (!nextName || nextName.length > 150) {
      setFeedback('姓名/昵称请填写 1–150 个字符。');
      return;
    }
    if (currentPassword || newPassword || confirmationPassword) {
      setFeedback('请先完成或清空改密表单，再保存个人资料。');
      return;
    }
    running.current = true;
    setBusy(true);
    setFeedback('');
    const context = generation.current;
    const controller = new AbortController();
    operation.current = controller;
    let committed = false;
    try {
      await accountRequest(
        api,
        `${base}/me`,
        {
          method: 'PATCH',
          body: JSON.stringify({ expectedVersion: profile.version, displayName: nextName }),
        },
        controller.signal,
      );
      committed = true;
      if (controller.signal.aborted || generation.current !== context) return;
      const confirmed = await read(controller.signal, context);
      if (controller.signal.aborted || generation.current !== context) return;
      if (
        !confirmed ||
        confirmed.displayName !== nextName ||
        confirmed.version <= profile.version
      ) {
        setLoadError('个人资料保存结果尚未确认，请重新读取。');
        setFeedback('已提交资料，但尚未读回本次变更。');
        return;
      }
      onProfileChanged?.({ displayName: confirmed.displayName });
      setFeedback('个人资料已保存，并已读回确认。');
    } catch (error) {
      if (!controller.signal.aborted && generation.current === context) {
        if (committed || !(error instanceof ApiError) || error.status >= 500)
          setLoadError('保存结果尚未确认，请重新读取后再操作。');
        setFeedback(
          committed
            ? '资料已提交，但重新读取未完成。'
            : accountError(error, '个人资料保存失败，请重试。'),
        );
      }
    } finally {
      if (generation.current === context && mounted.current) {
        running.current = false;
        setBusy(false);
      }
      if (operation.current === controller) operation.current = undefined;
    }
  }
  function askPasswordChange() {
    if (blocked || !profile || !canChangePassword) return;
    if (displayName !== profile.displayName) {
      setPasswordError('请先保存或恢复个人资料，再修改密码。');
      return;
    }
    const issue = !currentPassword
      ? '请输入当前登录密码。'
      : passwordProblem(newPassword, confirmationPassword);
    if (issue) {
      setPasswordError(issue);
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('新密码不能与当前密码相同。');
      return;
    }
    setPasswordError('');
    setConfirmPasswordChange(true);
  }
  async function changePassword() {
    if (running.current || blocked || !profile || !canChangePassword) return;
    const issue = !currentPassword
      ? '请输入当前登录密码。'
      : passwordProblem(newPassword, confirmationPassword);
    if (issue || currentPassword === newPassword) {
      setPasswordError(issue || '新密码不能与当前密码相同。');
      setConfirmPasswordChange(false);
      return;
    }
    running.current = true;
    setBusy(true);
    setConfirmPasswordChange(false);
    setFeedback('');
    const context = generation.current;
    const controller = new AbortController();
    operation.current = controller;
    const secrets = [currentPassword, newPassword, confirmationPassword];
    try {
      const receipt = await accountRequest<unknown>(
        api,
        `${base}/password`,
        {
          method: 'POST',
          body: JSON.stringify({ expectedVersion: profile.version, currentPassword, newPassword }),
        },
        controller.signal,
      );
      if (controller.signal.aborted || generation.current !== context) return;
      if (
        !isRecord(receipt) ||
        receipt.reauthenticationRequired !== true ||
        receipt.id !== profile.id ||
        !Number.isInteger(receipt.sessionsRevokedCount) ||
        Number(receipt.sessionsRevokedCount) < 0 ||
        !Number.isInteger(receipt.version) ||
        Number(receipt.version) <= profile.version
      ) {
        setLoadError('改密结果尚未确认，请重新登录确认，勿重复提交。');
        setFeedback('密码修改已提交，尚未获得完整确认回执。');
        return;
      }
      clearPasswords();
      setReauthenticationRequired(true);
      setFeedback('密码已修改，所有旧会话已失效，请重新登录。');
      onPasswordChanged?.();
    } catch (error) {
      if (!controller.signal.aborted && generation.current === context) {
        if (!(error instanceof ApiError) || error.status >= 500)
          setLoadError('改密结果尚未确认，请重新读取或重新登录确认。');
        setFeedback(accountError(error, '密码修改失败，请重新输入后重试。', secrets));
      }
    } finally {
      if (generation.current === context && mounted.current) {
        clearPasswords();
        running.current = false;
        setBusy(false);
      }
      if (operation.current === controller) operation.current = undefined;
    }
  }
  return (
    <section className="account-center" aria-label="个人账号中心">
      <header className="account-center__heading">
        <div>
          <span className="account-center__eyebrow">我的账号</span>
          <h1>个人中心</h1>
          <p>管理自己的展示姓名与登录安全；岗位及数据范围由平台管理员维护。</p>
        </div>
        <button
          type="button"
          disabled={busy || loading || reauthenticationRequired}
          onClick={() => (dirty ? setPendingNavigation(() => () => void reload()) : void reload())}
        >
          重新读取
        </button>
      </header>
      {loading && <Notice>正在读取个人资料…</Notice>}
      {loadError && <Notice tone="error">{loadError}</Notice>}
      {feedback && (
        <Notice
          tone={
            feedback.includes('已保存') || feedback.includes('密码已修改') ? 'success' : 'warning'
          }
        >
          {feedback}
        </Notice>
      )}
      {reauthenticationRequired && (
        <a className="account-center__login" href="/admin/login">
          重新登录
        </a>
      )}
      {profile && (
        <div className="account-center__layout">
          <section className="account-center__card">
            <h2>账号资料</h2>
            <div className="account-center__identity">
              <span>{profile.displayName.slice(0, 1) || '我'}</span>
              <div>
                <strong>{profile.displayName}</strong>
                <small>
                  {reauthenticationRequired
                    ? '需要重新登录'
                    : profile.status === 'ACTIVE'
                      ? '当前已登录'
                      : '账号状态待确认'}
                </small>
              </div>
            </div>
            <dl>
              <div>
                <dt>登录账号</dt>
                <dd>{profile.username || '登录标识尚未开通'}</dd>
              </div>
              <div>
                <dt>所属组织</dt>
                <dd>{profile.organizationName || '尚未分配组织'}</dd>
              </div>
              <div>
                <dt>岗位角色</dt>
                <dd>{profile.roleNames.join('、') || '尚未分配岗位'}</dd>
              </div>
              <div>
                <dt>密码登录</dt>
                <dd>{canChangePassword ? '已开通' : '待平台管理员开通'}</dd>
              </div>
            </dl>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void saveProfile();
              }}
            >
              <label>
                姓名 / 昵称
                <input
                  aria-label="个人姓名或昵称"
                  value={displayName}
                  maxLength={150}
                  disabled={blocked}
                  onChange={(event) => setDisplayName(event.target.value)}
                  autoComplete="name"
                />
              </label>
              <p className="account-center__hint">
                仅修改展示姓名，不改变登录账号、组织和业务权限。
              </p>
              <ActionButton
                type="submit"
                className="account-center__primary"
                pending={busy}
                disabled={
                  blocked || displayName.trim() === profile.displayName || !displayName.trim()
                }
              >
                保存个人资料
              </ActionButton>
            </form>
          </section>
          <section className="account-center__card">
            <h2>登录安全</h2>
            <div className="account-center__security-note">
              修改密码后，当前及其他设备的登录会话都将失效，需要使用新密码重新登录。
            </div>
            <p className="account-center__hint">
              最近修改：{changedAtLabel(profile.passwordChangedAt)}
            </p>
            <form
              autoComplete="off"
              onSubmit={(event) => {
                event.preventDefault();
                askPasswordChange();
              }}
            >
              <label>
                当前密码
                <input
                  type="password"
                  aria-label="当前登录密码"
                  autoComplete="current-password"
                  value={currentPassword}
                  maxLength={128}
                  disabled={blocked || !canChangePassword}
                  onChange={(event) => {
                    setCurrentPassword(event.target.value);
                    setPasswordError('');
                  }}
                />
              </label>
              <label>
                新密码
                <input
                  type="password"
                  aria-label="新的登录密码"
                  autoComplete="new-password"
                  value={newPassword}
                  maxLength={128}
                  disabled={blocked || !canChangePassword}
                  onChange={(event) => {
                    setNewPassword(event.target.value);
                    setPasswordError('');
                  }}
                />
              </label>
              <label>
                确认新密码
                <input
                  type="password"
                  aria-label="再次确认新登录密码"
                  autoComplete="new-password"
                  value={confirmationPassword}
                  maxLength={128}
                  disabled={blocked || !canChangePassword}
                  onChange={(event) => {
                    setConfirmationPassword(event.target.value);
                    setPasswordError('');
                  }}
                />
              </label>
              {!canChangePassword && (
                <Notice>
                  登录标识或密码登录尚未开通，请联系平台管理员；展示姓名仍可在左侧修改。
                </Notice>
              )}
              <p className="account-center__hint">
                12–128
                个字符，包含英文大写、小写和数字，可使用符号。密码不会被再次显示或保存在页面设置中。
              </p>
              {passwordError && <Notice tone="error">{passwordError}</Notice>}
              <div className="account-center__actions">
                <button type="button" disabled={busy} onClick={clearPasswords}>
                  清空输入
                </button>
                <ActionButton
                  type="submit"
                  className="account-center__primary"
                  pending={busy}
                  disabled={blocked || !canChangePassword}
                >
                  修改登录密码
                </ActionButton>
              </div>
            </form>
          </section>
        </div>
      )}
      <ConfirmDialog
        open={confirmPasswordChange}
        title="确认修改自己的登录密码？"
        description="修改会立即注销当前和其他设备的旧会话。请确认已记住新密码，完成后重新登录；业务权限保持不变。"
        confirmLabel="确认修改并重新登录"
        pending={busy}
        onCancel={clearPasswords}
        onConfirm={() => void changePassword()}
      />
      <ConfirmDialog
        open={Boolean(pendingNavigation)}
        title="放弃未保存的输入？"
        description="当前还有未保存的个人资料或密码输入。继续会清空输入，不会保存密码。"
        confirmLabel="放弃并继续"
        onCancel={() => {
          setPendingNavigation(undefined);
          clearPasswords();
        }}
        onConfirm={() => {
          const action = pendingNavigation;
          setPendingNavigation(undefined);
          clearPasswords();
          if (profile) setDisplayName(profile.displayName);
          action?.();
        }}
      />
    </section>
  );
}
